import {Card, Theme} from "@radix-ui/themes";
import CustomReader from "../components/CustomReader";
import React, {useEffect, useState} from "react";
import BookService from "../api/bookService";
import {Book} from "../models/Book";
import {useParams} from "react-router";
import {AlertCircle, Check, LoaderCircle, TriangleAlert} from "lucide-react";
import { BookAnalysis } from "../models/BookAnalysis";

export default function BookView() {
    const [book, setBook] = useState<Book | null>(null);
    const bookService = new BookService();
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [bookAnalysed, setBookAnalysed] = useState<BookAnalysis | null>(null);
    const { bookId } = useParams<{ bookId: string }>(); // 'id'

    const CRITERIA_NAME = [
        {
            criteriaName: 'Linguagens',
            description: 'Se há claramente linguagem inadequada, palavrões, gírias, linguagem ofensiva, bullying, preconceito',
        },
        {
            criteriaName: 'Medo e Violência',
            description: 'Se há cenas de violência explícita, diálogos de tortura, assassinato, suicídio, ferimentos e sangue.',
        },
        {
            criteriaName: 'Bebidas, drogas e Cigarro',
            description: 'Se o conteúdo possui alguma apologia a drogas, bebida, cigarro ou alcoolismo. Ou se possuem personagens que são relatados como diferenciados por possuírem tais práticas.',
        },
        {
            criteriaName: 'Sexualidade, Nudez e Romance',
            description: 'Se o livro apresenta trechos de nudez, linguagem explícita, cenas de sexo, beijo, abuso sexual mesmo que indiretamente ou sugestivo.',
        },
        {
            criteriaName: 'Diversidade e Inclusão',
            description: 'Se o livro apresenta diversidade de povos, culturas, raças e tribos e de gênero. Verificar se todos são devidamente respeitados igualmente.',
        },
        {
            criteriaName: 'Respeito às Religiões e Crenças',
            description: 'Se fere diretamente alguma crença ou religião, seja por quebra de princípios ou até mesmo por humor.',
        },
        {
            criteriaName: 'Produtos e Compras',
            description: 'Se o conteúdo sugere consumismo exagerado de algum produto ou serviço, diretamente ou indiretamente.',
        },
    ];

    const checkIsNegativeFeedback = (feedbackString: string) =>{
        return feedbackString === 'negative' || feedbackString === 'negativo' || feedbackString === '-1';
    }

    const processAnalysis = (analysisResult: any) => {
        const groupedItems = analysisResult.detailedAnalysis.reduce((acc: any, item: any) => {
            const key = item.criteriaName || 'Outro';
            acc[key] = acc[key] || [];
                if (item.detailedAnalysis.length > 0) {
                    acc[key].push(
                    ...item.detailedAnalysis.map((detailedItem: any) => ({
                        originalSentence: detailedItem.originalSentence || 'Frase original não disponível',
                        analysis: detailedItem.analysis || 'Análise não disponível',
                        isNegativeFeedback: checkIsNegativeFeedback(detailedItem?.feedback)
                    }))
                );
                } else {
                    acc[key].push(
                        {
                            originalSentence: '-',
                            analysis: item.description,
                            isNegativeFeedback: false
                        })
                }
            return acc;
        }, {});
    
        return CRITERIA_NAME.map((criteria) => ({
            ...criteria,
            items: groupedItems[criteria.criteriaName] || [],
        }));
    };    

    useEffect(() => {
        setLoading(true)
        async function fetchBook() {
            try {
                const result = await bookService.getBookById(Number(bookId));
                setBook(result);

            } catch (fetchError: any) {
                setError(fetchError.message);
            }
            setLoading(false);
        }
        fetchBook();

        async function fetchBookAnalysis() {
            const analysisResult = await bookService.getBookAnalysis(Number(bookId));
            const processedAnalysis = processAnalysis(analysisResult);
            setBookAnalysed({ ...analysisResult, detailedAnalysis: processedAnalysis });
        }
        fetchBookAnalysis();
    }, [bookId]);
    return (
        <div className="App p-8">
                {loading ?
                <div className={'flex justify-center text-center min-h-screen'}>
                    <LoaderCircle className={'animate-spin mt-[180px]'} color="white" size={100} />
                </div>
                : undefined}
            {!loading && book && <div>
            <div className={'flex flex-row py-4 items-start text-left gap-4'}>
                        <div className={'bg-white p-2 shadow-mdr rounded-md'}>
                            <img src={book?.coverUrl} width={108}/>
                        </div>
                        <div>
                            <div className={'text-4xl font-semibold text-[#2294ae]'}>Título: {book.title}</div>
                            <div className={'text-2xl text-[#2294ae]'}>Autor: {book.authors}</div>
                            <div className={'text-2xl text-[#2294ae]'}>Ano: {book.year}</div>
                            {/* <div className={'bg-red-600 text-white mt-4 border-red-300 px-2 py text-center text-[15px] border rounded-lg w-[200px]'}>Não Recomendado</div> */}
                            {/*<div className={'flex flex-column gap-2 py-4'}>*/}
                            {/*    {book.categories.map((category, i) => {*/}
                            {/*        // Return the element. Also pass key*/}
                            {/*        return (<div className={'bg-white rounded-md p-2 text-sm'}>{category}</div>)*/}
                            {/*    })}*/}
                            {/*</div>*/}

                        </div>
            </div>
                    <div className={'flex flex-row items-left justify-space-between  w-full gap-4 mt-4'}>
                        <div className={'w-2/5 sticky top-4'}>
                            { book?.s3bookId  && <CustomReader s3BookId={book.s3bookId} title={book.title} />}
                        </div>
                        <Card className={'w-full text-left'}>
                            <div className={'bg-white p-4'}>
                                <div className={'text-[#2294ae] font-semibold text-2xl'}>Análise</div>
                                <div className={'font-semibold pt-2 text-[#2294ae] text-lg'}>Geral</div>
                                <div className={'pb-2'}>{bookAnalysed?.general}</div>
                                {bookAnalysed?.detailedAnalysis.map((criteria, i) => {
                                    return (
                                        <Card key={i} className={'bg-sky-100 rounded-md py-2 text-sm mt-2'}>
                                            <div className={'text-[#2294ae] font-semibold text-2xl'}>{criteria.criteriaName}</div>
                                            <div>{criteria.description}</div>
                                            <div className={'py-2 ml-4'}>
                                                {criteria.items.length > 0 ? (
                                                    criteria.items.map((item, idx) => (
                                                        <div key={idx} className="py-2">
                                                           
                                                            <div className="flex flex-row font-semibold align-center">
                                                            {item.isNegativeFeedback ? 
                                                                <div className="mr-2"> 
                                                                    <TriangleAlert className="text-orange-400"/> 
                                                                </div>: 
                                                                <div className="mr-2">
                                                                    <Check className="text-[#2294ae]"/>
                                                                </div>} <div className="text-[18px] font-normal "> Item {idx + 1} - {item.analysis} </div>
                                                            </div>
                                                            <p className="pl-8 mt leading-5">Frase Original: {item.originalSentence}</p>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className={'text-gray-500 italic'}>Análise não disponível.</p>
                                                )}
                                            </div>
                                        </Card>
                                    );
                                })}
                            </div>
                        </Card>
                    </div>
            </div>}
        </div>
    );
}